import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, AfterViewInit } from '@angular/core';

// Declaración para que TypeScript reconozca el objeto grecaptcha global
declare const grecaptcha: any;

@Component({
  selector: 'app-recaptcha',
  templateUrl: './recaptcha.component.html',
  styleUrls: ['./recaptcha.component.css']
})
export class RecaptchaComponent implements OnInit, AfterViewInit, OnDestroy {
  // Input para recibir la clave del sitio desde el componente padre
  @Input() siteKey!: string;

  // Nuevos Outputs para cada evento de reCAPTCHA
  @Output() recaptchaSuccess = new EventEmitter<string>(); // Emite el token en caso de éxito
  @Output() recaptchaExpired = new EventEmitter<void>();   // Emite cuando el token expira
  @Output() recaptchaError = new EventEmitter<void>();     // Emite en caso de error

  // ID único para el contenedor de reCAPTCHA, útil si tienes múltiples reCAPTCHAs en la misma página
  private widgetId: number | undefined;

  constructor() { }

  ngOnInit(): void {
    // Asegurarse de que la clave del sitio ha sido proporcionada
    if (!this.siteKey) {
      console.error('reCAPTCHA: La clave del sitio (siteKey) es obligatoria.');
    }
  }

  ngAfterViewInit(): void {
    // Espera a que la API de reCAPTCHA esté disponible.
    this.checkRecaptchaAvailability();
  }

  private checkRecaptchaAvailability(): void {
    if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise && grecaptcha.enterprise.render) {
      this.renderRecaptcha();
    } else {
      // Si la API aún no está disponible, intenta de nuevo en un corto período
      setTimeout(() => this.checkRecaptchaAvailability(), 200);
    }
  }

  private renderRecaptcha(): void {
    try {
      // Renderiza el widget de reCAPTCHA en el div especificado
      this.widgetId = grecaptcha.enterprise.render('recaptcha-container', {
        sitekey: this.siteKey,
        callback: (token: string) => this.onRecaptchaResolvedSuccess(token), // Renombrado para mayor claridad
        'expired-callback': () => this.onRecaptchaResolvedExpired(),      // Renombrado
        //'error-callback': () => this.onRecaptchaResolvedError()            // Renombrado
      });
    } catch (error) {
      console.error('Error al renderizar reCAPTCHA Enterprise: ', error);
      this.onRecaptchaResolvedError();
    }
  }

  // Se llama cuando el usuario completa el reCAPTCHA con éxito
  onRecaptchaResolvedSuccess(token: string): void {
    //console.log('reCAPTCHA token obtenido:', token);
    this.recaptchaSuccess.emit(token);
  }

  // Se llama cuando el token de reCAPTCHA expira
  onRecaptchaResolvedExpired(): void {
    console.warn('reCAPTCHA token expirado.');
    this.recaptchaExpired.emit(); // Emite sin valor
    this.resetRecaptcha();        // Reinicia el reCAPTCHA automáticamente
  }

  // Se llama en caso de error de reCAPTCHA
  onRecaptchaResolvedError(): void {
    console.error('Error al cargar reCAPTCHA.');
    this.recaptchaError.emit();   // Emite sin valor
    this.resetRecaptcha();        // Reinicia el reCAPTCHA automáticamente
  }

  /**
   * Método para resetear el reCAPTCHA programáticamente.
   * Útil después de un envío de formulario o un error.
   */
  public resetRecaptcha(): void {
    //Reiniciando reCAPTCHA Enterprise
    if (
      this.widgetId !== undefined &&
      typeof grecaptcha !== 'undefined' &&
      grecaptcha.enterprise &&
      grecaptcha.enterprise.reset
    ) {
      try {
        const scrollX = window.scrollX || document.documentElement.scrollLeft;
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        grecaptcha.enterprise.reset(this.widgetId);
        setTimeout(() => {
          window.scrollTo(scrollX, scrollY);
        }, 0);
        //reCAPTCHA Enterprise reiniciado exitosamente
        // Opcional: Si necesitas notificar que el token se ha limpiado, podrías emitir null aquí
        // this.recaptchaSuccess.emit(null); // Esto requeriría cambiar el tipo de recaptchaSuccess
      } catch (error) {
        console.error('Error al reiniciar reCAPTCHA Enterprise:', error);
        // Si falla el reset, intentar re-renderizar
        setTimeout(() => this.renderRecaptcha(), 200);
      }
    } else {
      //Widget ID no disponible o API no cargada, re-renderizando...
      setTimeout(() => this.renderRecaptcha(), 200);
    }
  }

  ngOnDestroy(): void {
    // Opcional: Limpiar el widget de reCAPTCHA si es necesario al destruir el componente
    if (this.widgetId !== undefined && typeof grecaptcha !== 'undefined' && grecaptcha.destroy) {
      grecaptcha.destroy(this.widgetId);
    }
  }
}